import { createContext, useContext, useEffect, useState, ReactNode, FC } from 'react';
import { onAuthStateChanged, signInAnonymously as firebaseSignInAnonymously, signOut, User, GoogleAuthProvider, signInWithPopup, createUserWithEmailAndPassword, signInWithEmailAndPassword, EmailAuthProvider, reauthenticateWithCredential, reauthenticateWithPopup, getAdditionalUserInfo } from 'firebase/auth';
import { auth, db } from '../firebaseConfig';
import { doc, onSnapshot, updateDoc, deleteDoc, collection, getDocs, writeBatch, getDoc } from 'firebase/firestore';
import { GlobalConfig } from '../types/admin';
import { Player } from '../types';

interface AuthContextProps {
    user: User | null;
    userData: Player | null;
    loading: boolean;
    signInAnonymously: () => Promise<void>;
    signInWithGoogle: () => Promise<void>;
    signUpWithEmail: (email: string, password: string) => Promise<void>;
    signInWithEmail: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    setUserData: (data: Player | null) => void;
    devLogin: () => Promise<void>;
    deleteAccount: (password?: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [userData, setUserData] = useState<Player | null>(null);
    const [loading, setLoading] = useState(true);

    // Listen for Auth changes
    useEffect(() => {
        const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
            // Si on est en mode "Dev User" (dummy), on ignore les changements auth réels
            if (user && (user as any).isAnonymous && (user as any).email === 'dev@padelbrothers.com') {
                return;
            }
            setUser(currentUser);
            if (!currentUser) {
                // Check for Dev Session persistence
                if (localStorage.getItem('is_dev_logged_in') === 'true') {
                    devLogin();
                } else {
                    setLoading(false);
                }
            }
            // If currentUser exists, we WAIT for the Firestore listener to set loading(false)
            // This prevents the "glitch" where we show the app (and thus Onboarding check) before having the profile
        });
        return () => unsubscribeAuth();
    }, []); // Run once

    // Listen for Firestore User Data changes (works for both Real and Dev users)
    useEffect(() => {
        if (user) {
            // SKIP FIRESTORE FOR DEV USERS to avoid Permission Errors
            if ((user as any).email === 'dev@padelbrothers.com') {
                setUserData({
                    id: user.uid,
                    name: user.displayName || 'Dev User',
                    email: user.email || 'dev@padelbrothers.com',
                    pseudo: 'DevUser',
                    firstName: 'Dev',
                    level: '5',
                    hand: 'right',
                    avatar: '',
                    rating: 1800,
                    rd: 350,
                    volatility: 0.06,
                    badges: [],
                    ratingHistory: [],
                } as Player);
                setLoading(false);
                return;
            }

            const userDocRef = doc(db, 'users', user.uid);
            const unsubscribeSnapshot = onSnapshot(userDocRef, async (docSnap) => {
                if (docSnap.exists()) {
                    let data = docSnap.data() as Player;

                    // OVERRIDE: Check Global Config for White Label "free_for_all" behavior
                    const configRef = doc(db, 'config', 'global');
                    getDoc(configRef).then(configSnap => {
                        if (configSnap.exists()) {
                            const gc = configSnap.data() as GlobalConfig;
                            if (gc.whiteLabel?.enabled && gc.whiteLabel.payment?.subscriptionsEnabled === false) {
                                if (gc.whiteLabel.payment.disabledBehavior === 'free_for_all') {
                                    const freePlan = gc.whiteLabel.payment.freeForAllPlanOverride || 'premium';
                                    data = {
                                        ...data,
                                        subscription: {
                                            ...data.subscription,
                                            plan: freePlan,
                                            provider: 'manual',
                                            autoRenew: true
                                        }
                                    } as Player;
                                }
                            }
                        }
                        setUserData(data);
                        setLoading(false);
                    }).catch(() => {
                        setUserData(data);
                        setLoading(false);
                    });

                    // Update lastActive timestamp for inactivity tracking (GDPR compliance)
                    // Only update if not already updated recently (within last hour) to avoid excessive writes
                    const lastActive = data.lastActive ? new Date(data.lastActive) : null;
                    const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);

                    if (!lastActive || lastActive < oneHourAgo) {
                        try {
                            updateDoc(userDocRef, {
                                lastActive: new Date().toISOString(),
                                // Reset inactivity warnings if user logs in
                                'inactivityStatus.warning1Sent': false,
                                'inactivityStatus.warning2Sent': false
                            });
                        } catch (error) {
                            console.error("Error updating lastActive:", error);
                        }
                    }
                } else {
                    // If the document does not exist
                    // KEY FIX: Check if this is just a cache miss. 
                    // If fromCache is true and it doesn't exist, it means we just don't have it LOCALLY yet.
                    // We should wait for the server response (fromCache=false) to confirm it strictly doesn't exist.
                    // This prevents showing the Onboarding screen ("flashing") while fetching the profile.
                    if (!(docSnap as any).metadata.fromCache) {
                        setUserData(null);
                        setLoading(false);
                    }
                    // Else: do nothing, keep loading=true while waiting for server sync
                }
            }, (error) => {
                console.error("Error listening to user data:", error);
                setLoading(false);
            });
            return () => unsubscribeSnapshot();
        } else {
            setUserData(null);
        }
    }, [user]);

    const signInAnonymously = async () => {
        setLoading(true);
        try {
            await firebaseSignInAnonymously(auth);
        } catch (error) {
            console.error("Error signing in anonymously:", error);
            setLoading(false);
            throw error;
        }
    };

    const signInWithGoogle = async () => {
        setLoading(true);
        try {
            const provider = new GoogleAuthProvider();
            const result = await signInWithPopup(auth, provider);

            // Check if user is new and if registrations are allowed
            const additionalUserInfo = getAdditionalUserInfo(result);
            if (additionalUserInfo?.isNewUser) {
                // Fetch Global Config directly to be sure (as AppContext might not be ready or we are in AuthContext)
                try {
                    const configSnap = await getDoc(doc(db, 'config', 'global'));
                    if (configSnap.exists()) {
                        const config = configSnap.data() as GlobalConfig;
                        if (config.allowRegistrations === false || config.allowGoogleRegistration === false) {
                            // Delete the user we just created to rollback
                            await result.user.delete();
                            await signOut(auth); // Ensure local cleanup
                            throw new Error("Les inscriptions sont temporairement fermées.");
                        }
                    }
                } catch (checkErr) {
                    console.error("Error checking registration config:", checkErr);
                    // If we can't check, we should probably fail safe? Or allow?
                    // Assuming if we fail to check config, we let it pass or fail?
                    // Safe approach: if explicitly caught error (like permissions), maybe just rethrow.
                    // But if it's the "Les inscriptions..." error, we need to propagate it.
                    if ((checkErr as any).message === "Les inscriptions sont temporairement fermées.") {
                        throw checkErr;
                    }
                }
            }
        } catch (error) {
            console.error("Error signing in with Google:", error);
            setLoading(false);
            throw error;
        }
    };

    const signUpWithEmail = async (email: string, password: string) => {
        setLoading(true);
        try {
            await createUserWithEmailAndPassword(auth, email, password);
        } catch (error) {
            console.error("Error signing up with email:", error);
            setLoading(false);
            throw error;
        }
    };

    const signInWithEmail = async (email: string, password: string) => {
        setLoading(true);
        try {
            await signInWithEmailAndPassword(auth, email, password);
        } catch (error) {
            console.error("Error signing in with email:", error);
            setLoading(false);
            throw error;
        }
    };

    const devLogin = async () => {
        setLoading(true);
        localStorage.setItem('is_dev_logged_in', 'true');

        // Try to retrieve existing Dev ID from localStorage
        let devUserId = localStorage.getItem('padel_dev_user_id');
        if (!devUserId) {
            devUserId = 'dev_user_' + Math.random().toString(36).substr(2, 9);
            localStorage.setItem('padel_dev_user_id', devUserId);
        }

        // On simule un utilisateur pour contourner les blocages Firebase Mobile temporaires
        const dummyUser: Partial<User> = {
            uid: devUserId,
            email: 'dev@padelbrothers.com',
            displayName: 'Dev User',
            emailVerified: true,
            isAnonymous: true,
            metadata: {} as any,
            providerData: [],
            refreshToken: '',
            tenantId: null,
            delete: async () => { },
            getIdToken: async () => '',
            getIdTokenResult: async () => ({} as any),
            reload: async () => { },
            toJSON: () => ({}),
            phoneNumber: null,
        };
        setUser(dummyUser as User);
        setLoading(false);
    };

    const logout = async () => {
        try {
            localStorage.removeItem('is_dev_logged_in');
            await signOut(auth);
            setUser(null);
            setUserData(null);
        } catch (error) {
            console.error("Error signing out:", error);
            throw error;
        }
    };

    const deleteAccount = async (password?: string) => {
        try {
            if (user) {
                const userId = user.uid;

                // Re-authenticate user before deletion (required by Firebase for sensitive operations)
                if (user.providerData[0]?.providerId === 'google.com') {
                    // For Google sign-in, re-authenticate with popup
                    const provider = new GoogleAuthProvider();
                    await reauthenticateWithPopup(user, provider);
                } else if (password && user.email) {
                    // For email/password sign-in
                    const credential = EmailAuthProvider.credential(user.email, password);
                    await reauthenticateWithCredential(user, credential);
                } else if (!password && user.email) {
                    // Password required but not provided
                    throw new Error('PASSWORD_REQUIRED');
                }

                // 1. Delete user document from Firestore
                await deleteDoc(doc(db, 'users', userId));

                // 2. Remove user from all groups they're part of
                const groupsSnapshot = await getDocs(collection(db, 'groups'));
                const batch = writeBatch(db);

                for (const groupDoc of groupsSnapshot.docs) {
                    const groupData = groupDoc.data();
                    // Check if user is in members array
                    if (groupData.members?.includes(userId)) {
                        const newMembers = groupData.members.filter((m: string) => m !== userId);
                        batch.update(groupDoc.ref, { members: newMembers });
                    }
                    // Delete from players sub-collection
                    const playerDocRef = doc(db, 'groups', groupDoc.id, 'players', userId);
                    batch.delete(playerDocRef);
                }

                await batch.commit();

                // 3. Delete Firebase Auth user
                await user.delete();

                setUser(null);
                setUserData(null);
            }
        } catch (error) {
            console.error("Error deleting account:", error);
            throw error;
        }
    };

    const contextValue = {
        user,
        userData,
        loading,
        signInAnonymously,
        signInWithGoogle,
        signUpWithEmail,
        signInWithEmail,
        logout,
        setUserData,
        devLogin,
        deleteAccount
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error("useAuth must be used within an AuthProvider");
    return context;
};